import type { Scene } from './types';
import { spawnCasing, spawnMuzzlePuff } from './effects';
import { SHOT_SFX, cull, type Mix } from './battleConfig';
import { playSfx } from '../game/sfx';
import type { PlaybackState } from './playbackState';
import type { Actors } from './actors';

// last time a casing-drop tink played — throttles a burst's shells into a few clinks, not a pile
let lastCasingLandAt = -1;

/** Fire every timeline entry + deferred grenade blast / burst shot / smoke bloom that is now due. */
export const runDueEvents = (sc: Scene, state: PlaybackState, n: number, mix: Mix, actors: Actors, youId: string | null): void => {
  while (state.timeline.length && state.timeline[0].at <= n) state.timeline.shift()?.run(n);

  for (let i = state.pendingBlasts.length - 1; i >= 0; i--) {
    if (n < state.pendingBlasts[i].at) continue;
    const b = state.pendingBlasts.splice(i, 1)[0];
    // painter-sort anchor: the lowest struck fighter's feet, so the blast interleaves with
    // bodies by depth (in front of who it hits, behind anyone closer) — same as smoke
    const groundY = Math.max(b.cy, ...b.ev.hits.map((h) => sc.byId[h.id]?.y ?? b.cy));
    sc.blasts.push({ x: b.cx, y: b.cy, t0: n, life: 0.6, r: 150, groundY });
    playSfx('grenade_explode', 0.5);
    // hp/armor land through the cursor at LANDING time — committing at the throw would
    // apply the damage half a second early
    actors.commit(b.ev);
    for (const hit of b.ev.hits) {
      const f = sc.byId[hit.id];
      if (!f) continue;
      // the adrenaline shield bar eats its part first (amber pop), the rest is the damage pop
      const toHp = actors.drainHit(f, hit.damage, hit.absorbed);
      if (toHp > 0) actors.popDamage(f, toHp, false);
    }
  }

  for (let i = state.pendingShots.length - 1; i >= 0; i--) {
    if (n < state.pendingShots[i].at) continue;
    const ps = state.pendingShots.splice(i, 1)[0];
    sc.bullets.push({ fromX: ps.fromX, fromY: ps.fromY, toX: ps.toX, toY: ps.toY, t0: n, life: 0.12 });
    sc.flashes.push({ x: ps.fromX, y: ps.fromY, angle: ps.angle, t0: n, life: 0.07 });
    const shooter = sc.byId[ps.shooter];
    spawnMuzzlePuff(sc, ps.fromX, ps.fromY, ps.angle, shooter ? shooter.y : ps.fromY, n);
    if (shooter) spawnCasing(sc, ps.fromX, ps.fromY, ps.angle, shooter.dir, shooter.y, ps.shooter === youId, n);
    playSfx(SHOT_SFX[ps.key] ?? SHOT_SFX.ak, mix(0.3, ps.shooter, ps.target));
  }

  // spent shells clink when they first hit the ground; throttle so a burst's shells (all landing
  // within a few frames) read as a couple of clinks, not a wall of noise
  for (const c of sc.casings) {
    if (c.landed || n < c.t0 + c.landT) continue;
    c.landed = true;
    // your own shell drops a touch quieter than before; an enemy's is ~3× quieter still
    if (Math.abs(n - lastCasingLandAt) >= 0.05) {
      lastCasingLandAt = n;
      const vol = c.mine ? 0.17 + Math.random() * 0.05 : 0.06 + Math.random() * 0.03;
      playSfx('casing_drop', vol);
    }
  }

  for (let i = state.pendingSmokes.length - 1; i >= 0; i--) {
    if (n < state.pendingSmokes[i].at) continue;
    const sm = state.pendingSmokes.splice(i, 1)[0];
    // the canister landed — wake the target's standing screen: invisible until this instant,
    // it blooms in from here and stands through its whole coverage (no transient twin cloud)
    const scr = state.smokeScreens.get(sm.target);
    if (scr && scr.bornAt == null) scr.bornAt = n;
    playSfx('smoke_bloom', 0.45); // soft "pfff" as the cloud pops open
  }
};

/** Drop every transient effect whose life has elapsed, plus grenades past their landing time. */
export const cullEffects = (sc: Scene, n: number): void => {
  cull(sc.bullets, n); cull(sc.casings, n); cull(sc.muzzlePuffs, n); cull(sc.flashes, n); cull(sc.impacts, n); cull(sc.blasts, n); cull(sc.damages, n);
  cull(sc.slashes, n); cull(sc.flashbangs, n);
  // a grenade reaching its landing time = it touched the ground → dull thud (every type)
  for (let i = sc.grenades.length - 1; i >= 0; i--) if (n >= sc.grenades[i].land) { sc.grenades.splice(i, 1); playSfx('grenade_land', 0.4); }
};
